import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Modal, Form, Input, Button, message, Upload, UploadFile, Checkbox } from "antd";
import { InboxOutlined } from "@ant-design/icons";
// UUID
import { v4 as uuidv4 } from "uuid";
// REDUX
import {
  usePostMapViewMutation,
  usePutMapViewMutation,
  useDeleteMapViewMutation,
} from "../../../redux/mapView/mapViewAPI";
// AUTHENTICATION
import GETJwtToken from "../../../redux/authentication/authentication";
import type { MapMetaData } from "./types";

interface CreateMapModalProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  editingMap?: MapMetaData | null;
  onMapCreated: () => void;
}

const stringifyMapPayloadForLog = (value: unknown) =>
  JSON.stringify(
    value,
    (_, currentValue) =>
      typeof currentValue === "bigint" ? currentValue.toString() : currentValue,
    2,
  );

function CreateMapModal({
  open,
  onClose,
  organizationId,
  editingMap = null,
  onMapCreated,
}: CreateMapModalProps) {
  // ************************************************ */
  // FORM AND STATE ******************************* */
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const isEditMode = Boolean(editingMap?.id);

  // ************************************************ */
  // REDUX AND API CALLS ************************** */
  const [triggerPostMapView, { isLoading: isLoadingPost }] = usePostMapViewMutation();
  const [triggerPutMapView, { isLoading: isLoadingPut }] = usePutMapViewMutation();
  const [triggerDeleteMapView, { isLoading: isLoadingDelete }] = useDeleteMapViewMutation();

  const extractUploadUrl = (payload: any): string | null => {
    if (!payload) {
      return null;
    }

    const candidate =
      payload.url ||
      payload.map_img_url ||
      payload.mapImgUrl ||
      payload?.data?.url ||
      payload?.data?.map_img_url ||
      payload?.data?.mapImgUrl;

    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate;
    }

    return null;
  };

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const uploadSvgFile = async (file: File): Promise<string | null> => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const token = await GETJwtToken();
      const response = await fetch(
        `${process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE}/maps/upload-svg`,
        {
          method: "POST",
          headers: {
            Authorization: token,
          },
          body: formData,
        },
      );

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const contentType = (response.headers.get("content-type") || "").toLowerCase();

      if (contentType.includes("application/json")) {
        const data = await response.json();
        return extractUploadUrl(data);
      }

      const rawText = (await response.text()).trim();

      if (!rawText) {
        const locationHeader = response.headers.get("location");
        return locationHeader && locationHeader.trim().length > 0 ? locationHeader : null;
      }

      try {
        const parsed = JSON.parse(rawText);
        return extractUploadUrl(parsed);
      } catch {
        return rawText;
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to upload SVG";
      message.error(`SVG upload failed: ${errorMsg}`);
      return null;
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    const initialValues = {
      mapName: editingMap?.mapName ?? "",
      hasDisplayView: Boolean(editingMap?.hasDisplayView ?? false),
      isMainMap: Boolean(
        editingMap?.isMain ?? editingMap?.main ?? editingMap?.hasDisplayView ?? false,
      ),
    };

    form.setFieldsValue(initialValues);

    if (isEditMode && editingMap?.mapImgUrl) {
      setFileList([]);
    } else {
      setFileList([]);
    }
  }, [open, editingMap, form, isEditMode]);

  const handleCreateMap = async () => {
    try {
      const values = await form.validateFields();

      let mapImgUrl = isEditMode ? (editingMap?.mapImgUrl ?? "") : "";

      if (fileList.length > 0) {
        const file = fileList[0] as UploadFile;
        if (file.originFileObj) {
          setUploading(true);
          const uploadedUrl = await uploadSvgFile(file.originFileObj as File);
          setUploading(false);

          if (!uploadedUrl) {
            message.error("Failed to upload SVG file");
            return;
          }
          mapImgUrl = uploadedUrl;
        }
      }

      const token = await GETJwtToken();
      const payload = {
        id: isEditMode ? editingMap?.id : uuidv4(),
        name: values.mapName,
        org_id: organizationId,
        map_img_url: mapImgUrl,
        has_display_view: Boolean(values.hasDisplayView),
        is_main: Boolean(values.isMainMap),
        offline_url: "",
        waypoints: [],
        group_waypoints: [],
        bank_waypoints: [],
      };

      // Helpful while validating the create/edit save flow.
      // eslint-disable-next-line no-console
      console.log("Saving map payload:", stringifyMapPayloadForLog(payload));

      if (isEditMode && editingMap?.id) {
        await triggerPutMapView({
          token,
          id: editingMap.id,
          body: payload,
        }).unwrap();

        message.success("Map updated successfully");
      } else {
        await triggerPostMapView({
          token,
          body: payload,
        }).unwrap();

        message.success("Map created successfully");
      }

      form.resetFields();
      setFileList([]);
      onClose();
      onMapCreated();
    } catch (err: any) {
      const errorMsg =
        typeof err === "string"
          ? err
          : err?.data?.message || err?.message || "Failed to save map. Please try again.";
      message.error(errorMsg);
    }
  };

  const handleBeforeUpload = () => false;

  const handleFileChange = (info: { fileList: UploadFile[] }) =>
    setFileList(info.fileList.slice(-1));

  const handleDeleteMap = () => {
    if (!isEditMode || !editingMap?.id) {
      return;
    }

    Modal.confirm({
      title: "Delete Map",
      content: "Are you sure you want to delete this map? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const token = await GETJwtToken();
          await triggerDeleteMapView({ token, id: editingMap.id }).unwrap();

          message.success("Map deleted successfully");
          form.resetFields();
          setFileList([]);
          onClose();
          onMapCreated();
        } catch (err: any) {
          const errorMsg =
            typeof err === "string"
              ? err
              : err?.data?.message || err?.message || "Failed to delete map. Please try again.";
          message.error(errorMsg);
        }
      },
    });
  };

  // ************************************************ */
  // RENDERING ************************************** */
  return (
    <Modal
      title={isEditMode ? "Edit Map" : "Create Map"}
      open={open}
      onCancel={() => {
        form.resetFields();
        setFileList([]);
        onClose();
      }}
      footer={[
        ...(isEditMode
          ? [
              <Button key="delete" danger loading={isLoadingDelete} onClick={handleDeleteMap}>
                Delete
              </Button>,
            ]
          : []),
        <Button
          key="cancel"
          onClick={() => {
            form.resetFields();
            setFileList([]);
            onClose();
          }}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isLoadingPost || isLoadingPut || uploading}
          onClick={handleCreateMap}
        >
          {isEditMode ? "Save" : "Create"}
        </Button>,
      ]}
      width={500}
    >
      <Form form={form} layout="vertical" style={{ paddingTop: "8px" }}>
        <Form.Item
          label="Map Name"
          name="mapName"
          rules={[{ required: true, message: "Please enter a map name" }]}
        >
          <Input placeholder="Enter map name" />
        </Form.Item>

        <Form.Item name="hasDisplayView" valuePropName="checked" style={{ marginBottom: 6 }}>
          <Checkbox>Has Display View</Checkbox>
        </Form.Item>

        <Form.Item name="isMainMap" valuePropName="checked" style={{ marginBottom: 10 }}>
          <Checkbox>Set as Main Map</Checkbox>
        </Form.Item>

        {isEditMode && editingMap?.mapImgUrl ? (
          <Form.Item label="Current SVG">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                padding: 12,
                background: "#fafafa",
                minHeight: 110,
              }}
            >
              <img
                src={editingMap.mapImgUrl}
                alt="Map preview"
                style={{
                  maxWidth: 180,
                  maxHeight: 110,
                  objectFit: "contain",
                }}
              />
            </div>
          </Form.Item>
        ) : null}

        <Form.Item label="Upload Map SVG" name="mapSvg">
          <Upload
            maxCount={1}
            accept=".svg"
            beforeUpload={handleBeforeUpload}
            onChange={handleFileChange}
            fileList={fileList}
            listType="picture"
          >
            <div
              style={{
                padding: "20px",
                border: "1px dashed #d9d9d9",
                borderRadius: "4px",
                textAlign: "center",
              }}
            >
              <InboxOutlined style={{ fontSize: "32px", marginBottom: "8px" }} />
              <div>{isEditMode ? "Replace SVG file" : "Click or drag SVG file here"}</div>
              <div style={{ fontSize: "12px", color: "#999", marginTop: "8px" }}>
                Only SVG files are supported
              </div>
            </div>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default CreateMapModal;
